import { CheckOutlined, EyeOutlined, ReloadOutlined, ShareAltOutlined, StarOutlined, TruckOutlined, ShoppingOutlined, DollarOutlined, CalendarOutlined, EnvironmentOutlined, FileTextOutlined } from '@ant-design/icons';
import { Button, Card, Checkbox, Col, Descriptions, Divider, Form, Input, List, message, Modal, Pagination, Rate, Row, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useUserOrders } from '../hooks/useApi';
import { useCancelOrder, useUpdateOrderStatus } from '../hooks/useApiMutations';
import { useIsMobile } from '../hooks/useIsMobile';
import { useI18n } from '../i18n';
import { apiService, Order } from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

// 发货信息类型定义
interface ShippingInfo {
  id: number;
  trackingNumber: string;
  carrierName: string;
  shippingAddress: string;
  deliveryAddress: string;
  contactPhone: string;
  contactName: string;
  status: string;
  shippedAt: string;
  deliveredAt?: string;
  shippingNotes?: string;
}

const OrderManagement: React.FC = () => {
  const { t } = useI18n();

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [shippingInfo, setShippingInfo] = useState<ShippingInfo[]>([]);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [socialShareModalVisible, setSocialShareModalVisible] = useState(false);
  const [socialShareForm] = Form.useForm();
  const [currentOrderForShare, setCurrentOrderForShare] = useState<Order | null>(null);
  const [existingShares, setExistingShares] = useState<any[]>([]);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [reviewForm] = Form.useForm();
  const [currentOrderForReview, setCurrentOrderForReview] = useState<Order | null>(null);
  const [currentReviewGoodsIndex, setCurrentReviewGoodsIndex] = useState<number>(0);
  const [reviewedGoods, setReviewedGoods] = useState<Set<number>>(new Set());
  const isMobile = useIsMobile();

  const pageSize = 10;

  // 使用SWR获取订单数据
  const {
    orders: allOrders,
    totalElements,
    isLoading: ordersLoading,
    error: ordersError,
    mutate: refreshOrders
  } = useUserOrders(currentPage - 1, pageSize);

  // 使用mutations
  const { mutate: cancelOrder, isLoading: cancelLoading } = useCancelOrder();
  const { mutate: updateOrderStatus, isLoading: updateLoading } = useUpdateOrderStatus();

  const orders = allOrders;
  const loading = ordersLoading || cancelLoading || updateLoading;

  // 订单状态映射
  const statusMap: Record<string, { color: string; text: string }> = {
    PENDING: { color: 'orange', text: t('messages.orders.status.pending') },
    CONFIRMED: { color: 'blue', text: t('messages.orders.status.confirmed') },
    PROCESSING: { color: 'cyan', text: t('messages.orders.status.processing') },
    SHIPPED: { color: 'purple', text: t('messages.orders.status.shipped') },
    DELIVERED: { color: 'green', text: t('messages.orders.status.delivered') },
    CANCELLED: { color: 'red', text: t('messages.orders.status.cancelled') },
    REFUNDED: { color: 'volcano', text: t('messages.orders.status.refunded') },
  };

  // 处理错误
  useEffect(() => {
    if (ordersError) {
      message.error(t('messages.orders.loadFailed'));
      console.error('Error loading orders:', ordersError);
    }
  }, [ordersError, t]);

  // 调试订单数据
  useEffect(() => {
    if (selectedOrder) {
      console.log('Selected Order:', selectedOrder);
      console.log('Order Items:', selectedOrder.items);
      console.log('Order OrderItems:', selectedOrder.orderItems);
    }
  }, [selectedOrder]);

  // 当切换商品或打开评价弹窗时，加载已有评价
  useEffect(() => {
    const loadReview = async () => {
      if (reviewModalVisible && currentOrderForReview) {
        const orderItems = currentOrderForReview.items || currentOrderForReview.orderItems || [];
        const currentItem = orderItems[currentReviewGoodsIndex];

        if (currentItem?.goods?.id) {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            try {
              const user = JSON.parse(userStr);
              if (user.id) {
                await loadExistingReview(currentItem.goods.id, user.id);
              }
            } catch (e) {
              console.error('解析用户信息失败:', e);
            }
          }
        }
      }
    };

    loadReview();
  }, [reviewModalVisible, currentReviewGoodsIndex, currentOrderForReview]);

  // 查看订单详情
  const handleViewOrder = async (orderId: number) => {
    // 从当前订单列表中找到对应的订单
    const order = allOrders.find(o => o.id === orderId);
    if (order) {
      setSelectedOrder(order);
      setDetailVisible(true);

      // 获取发货信息
      if (order.status === 'SHIPPED' || order.status === 'DELIVERED') {
        setShippingLoading(true);
        try {
          const response = await apiService.getOrderShippingInfo(order.id);
          if (response.success) {
            setShippingInfo(response.data || []);
          }
        } catch (error) {
          console.error('获取发货信息失败:', error);
          setShippingInfo([]);
        } finally {
          setShippingLoading(false);
        }
      } else {
        setShippingInfo([]);
      }
    } else {
      message.error(t('messages.orders.loadDetailFailed'));
    }
  };

  // 取消订单
  const handleCancelOrder = async (orderId: number) => {
    // 查找订单信息
    const order = allOrders?.find(o => o.id === orderId);
    if (!order) {
      message.error(t('messages.orders.orderNotFound'));
      return;
    }

    // 检查订单状态，只有在未发货状态下才能取消
    if (order.status === 'SHIPPED' || order.status === 'DELIVERED') {
      message.warning(t('messages.orders.cancelNotAllowed'));
      return;
    }

    if (order.status === 'CANCELLED') {
      message.warning(t('messages.orders.orderAlreadyCancelled'));
      return;
    }

    Modal.confirm({
      title: t('messages.orders.cancelConfirmTitle'),
      content: t('messages.orders.cancelConfirmContent'),
      onOk: async () => {
        try {
          await cancelOrder({ orderId, reason: t('messages.orders.cancelReason') });
          message.success(t('messages.orders.cancelSuccess'));
          refreshOrders(); // 刷新订单列表
        } catch (error) {
          message.error(t('messages.orders.cancelFailed'));
          console.error('Error cancelling order:', error);
        }
      },
    });
  };

  // 确认签收
  const handleConfirmDelivery = async (orderId: number) => {
    Modal.confirm({
      title: t('messages.orders.confirmDelivery.title'),
      content: t('messages.orders.confirmDelivery.content'),
      okText: t('messages.orders.confirmDelivery.okText'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await updateOrderStatus({ orderId, status: 'DELIVERED', notes: t('messages.orders.confirmDelivery.notes') });
          message.success(t('messages.orders.confirmDelivery.success'));
          refreshOrders(); // 刷新订单列表

          // 找到对应的订单并显示社交媒体分享模态框
          const order = allOrders?.find(o => o.id === orderId);
          if (order) {
            setCurrentOrderForShare(order);
            // 加载已有的分享记录
            const sharesResponse = await apiService.getOrderSocialShares(orderId);
            if (sharesResponse.success) {
              setExistingShares(sharesResponse.data || []);
            }
            setSocialShareModalVisible(true);
          }
        } catch (error) {
          message.error(t('messages.orders.confirmDelivery.failed'));
          console.error('Error confirming delivery:', error);
        }
      },
    });
  };

  // 处理社交媒体分享提交
  const handleSocialShareSubmit = async (values: any) => {
    if (!currentOrderForShare) return;

    try {
      const response = await apiService.createSocialShare(
        currentOrderForShare.id,
        values.platform,
        values.shareUrl,
        values.description
      );

      if (response.success) {
        message.success(t('messages.orders.socialShare.submitSuccess'));
        socialShareForm.resetFields();
        // 刷新已有的分享记录
        if (currentOrderForShare) {
          const sharesResponse = await apiService.getOrderSocialShares(currentOrderForShare.id);
          if (sharesResponse.success) {
            setExistingShares(sharesResponse.data || []);
          }
        }
      } else {
        message.error(response.message || t('messages.orders.socialShare.submitFailed'));
      }
    } catch (error) {
      message.error(t('messages.orders.socialShare.submitFailed'));
      console.error('Error submitting social share:', error);
    }
  };

  // 加载用户对当前商品的已有评价
  const loadExistingReview = async (goodsId: number, userId: number) => {
    try {
      // 获取该用户对该商品的评价
      const response = await apiService.getUserGoodsReview(goodsId, userId);

      if (response.success && response.data) {
        const existingReview = response.data;
        // 将已有评价填充到表单中
        reviewForm.setFieldsValue({
          rating: existingReview.rating,
          content: existingReview.content,
          isAnonymous: existingReview.isAnonymous
        });
        message.info(t('messages.orders.review.loadedExisting'));
        return existingReview;
      }
      return null;
    } catch (error) {
      console.error('加载已有评价失败:', error);
      return null;
    }
  };

  // 处理单个商品评论提交
  const handleReviewSubmit = async (values: any) => {
    if (!currentOrderForReview) return;

    try {
      // 获取订单商品列表（处理 items 和 orderItems 两种情况）
      const orderItems = currentOrderForReview.items || currentOrderForReview.orderItems || [];

      if (orderItems.length === 0) {
        message.error(t('messages.orders.review.noProducts'));
        return;
      }

      // 获取当前要评价的商品
      const currentItem = orderItems[currentReviewGoodsIndex];
      if (!currentItem) {
        message.error(t('messages.orders.review.productNotFound'));
        return;
      }

      // 验证评分
      if (!values.rating || values.rating === 0) {
        message.error(t('messages.orders.review.ratingRequired'));
        return;
      }

      // 获取当前用户ID
      const userStr = localStorage.getItem('user');
      let userId = 0;
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          userId = user.id || 0;
        } catch (e) {
          console.error('解析用户信息失败:', e);
        }
      }

      if (!userId) {
        message.error(t('messages.orders.review.noUserInfo'));
        return;
      }

      // 获取商品ID（从goods对象中获取）
      if (!currentItem.goods || !currentItem.goods.id) {
        console.error('商品信息无效，order_items缺少goods对象或goods.id:', currentItem);
        message.error(t('messages.orders.review.invalidProductId'));
        return;
      }

      const goodsId = currentItem.goods.id;
      console.log('当前商品完整信息:', currentItem);
      console.log('提取的goodsId:', goodsId);

      console.log('准备提交评价，参数:', {
        goodsId,
        userId,
        rating: Number(values.rating),
        content: values.content || '',
        isAnonymous: values.isAnonymous || false
      });

      // 提交当前商品的评价
      const result = await apiService.createProductReview(
        goodsId,
        userId,
        Number(values.rating),
        values.content || '',
        values.isAnonymous || false
      );

      console.log('评价提交返回结果:', result);

      if (result && (result.message || result.success || result.data)) {
        // 显示成功消息（区分新建和更新）
        if (result.isUpdate) {
          message.success(t('messages.orders.review.reviewUpdated'));
        } else {
          message.success(t('messages.orders.review.submitSuccess'));
        }

        // 标记当前商品已评价
        const newReviewedGoods = new Set(reviewedGoods);
        newReviewedGoods.add(currentReviewGoodsIndex);
        setReviewedGoods(newReviewedGoods);

        // 清空表单
        reviewForm.resetFields();

        // 检查是否还有未评价的商品
        const nextIndex = currentReviewGoodsIndex + 1;
        if (nextIndex < orderItems.length) {
          // 切换到下一个商品
          setCurrentReviewGoodsIndex(nextIndex);
          message.info(t('messages.orders.review.switchedToNext', { next: nextIndex + 1 }));
        } else {
          // 所有商品都已评价完成
          message.success(t('messages.orders.review.allReviewsCompleted'));
          setReviewModalVisible(false);
          setCurrentReviewGoodsIndex(0);
          setReviewedGoods(new Set());
          refreshOrders(); // 刷新订单列表
        }
      } else {
        message.error(t('messages.orders.review.submitFailed'));
      }
    } catch (error: any) {
      message.error(error.message || t('messages.orders.review.submitFailed'));
      console.error('Error submitting review:', error);
    }
  };


  // 分页处理
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // 表格列定义
  const columns = [
    {
      title: t('messages.orders.table.orderNumber'),
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 150,
      render: (text: string) => (
        <Text code style={{ fontSize: '12px' }}>
          {text}
        </Text>
      ),
    },
    {
      title: t('messages.orders.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const statusInfo = statusMap[status] || { color: 'default', text: status };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: t('messages.orders.table.totalAmount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      render: (amount: number, record: any) => {
        const discount = record.totalDiscount || 0;
        const originalAmount = amount;
        const finalAmount = Math.max(0, amount - discount);
        return (
          <div>
            {discount > 0 ? (
              <>
                <div style={{ textDecoration: 'line-through', color: '#999', fontSize: '12px' }}>
                  {t('messages.orders.details.originalPrice')}: NT${originalAmount.toFixed(2)}
                </div>
                <div style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                  {t('messages.orders.details.total')}: NT${finalAmount.toFixed(2)}
                </div>
                <div style={{ color: '#52c41a', fontSize: '12px' }}>
                  {t('messages.orders.details.discount')}: NT${discount.toFixed(2)}
                </div>
              </>
            ) : (
              <Text strong style={{ color: '#ff4d4f' }}>
                NT${finalAmount.toFixed(2)}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: t('messages.orders.table.itemCount'),
      dataIndex: 'items',
      key: 'itemCount',
      width: 100,
      render: (items: any[]) => items?.length || 0,
    },
    {
      title: t('messages.orders.table.shippingAddress'),
      dataIndex: 'shippingAddress',
      key: 'shippingAddress',
      ellipsis: true,
    },
    {
      title: t('messages.orders.table.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: t('messages.orders.table.actions'),
      key: 'actions',
      width: 250,
      render: (_: any, record: Order) => (
        <Space className="mobile-actions" wrap>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewOrder(record.id)}
            style={{
              whiteSpace: 'nowrap',
              borderRadius: '6px',
              fontWeight: '500'
            }}
          >
            {t('messages.orders.table.view')}
          </Button>
          {/* 取消订单按钮 - 只在未发货状态下显示 */}
          {(record.status === 'PENDING' || record.status === 'CONFIRMED' || record.status === 'PROCESSING') && (
            <Button
              type="default"
              size="small"
              danger
              onClick={() => handleCancelOrder(record.id)}
              style={{
                whiteSpace: 'nowrap',
                borderRadius: '6px',
                fontWeight: '500'
              }}
            >
              {t('messages.orders.table.cancelOrder')}
            </Button>
          )}
          {record.status === 'SHIPPED' && (
            <Button
              type="default"
              size="small"
              icon={<CheckOutlined />}
              onClick={() => handleConfirmDelivery(record.id)}
              style={{
                whiteSpace: 'nowrap',
                borderRadius: '6px',
                fontWeight: '500',
                borderColor: '#52c41a',
                color: '#52c41a'
              }}
            >
              {t('messages.orders.confirmDelivery.button')}
            </Button>
          )}
          {record.status === 'DELIVERED' && (
            <>
              <Button
                type="default"
                size="small"
                icon={<StarOutlined />}
                onClick={() => {
                  setCurrentOrderForReview(record);
                  setReviewModalVisible(true);
                }}
                style={{
                  whiteSpace: 'nowrap',
                  borderRadius: '6px',
                  fontWeight: '500',
                  borderColor: '#faad14',
                  color: '#faad14'
                }}
              >
                写评论
              </Button>
              <Button
                type="default"
                size="small"
                icon={<ShareAltOutlined />}
                onClick={async () => {
                  setCurrentOrderForShare(record);
                  // 加载已有的分享记录
                  const sharesResponse = await apiService.getOrderSocialShares(record.id);
                  if (sharesResponse.success) {
                    setExistingShares(sharesResponse.data || []);
                  }
                  setSocialShareModalVisible(true);
                }}
                style={{
                  whiteSpace: 'nowrap',
                  borderRadius: '6px',
                  fontWeight: '500',
                  borderColor: '#1890ff',
                  color: '#1890ff'
                }}
              >
                分享体验
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  // 移动端订单组件
  const MobileOrderCard = ({ order }: { order: Order }) => (
    <div
      style={{
        padding: '16px',
        borderBottom: '1px solid #f0f0f0',
        backgroundColor: '#fff'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div>
          <Text strong style={{ fontSize: '14px' }}>{order.orderNumber}</Text>
          <div style={{ marginTop: '4px' }}>
            <Tag color={statusMap[order.status]?.color || 'default'}>
              {statusMap[order.status]?.text || order.status}
            </Tag>
          </div>
        </div>
        <Text style={{ fontSize: '12px', color: '#666' }}>
          {new Date(order.createdAt).toLocaleString()}
        </Text>
      </div>

      <div style={{ marginBottom: '12px' }}>
        <Text strong style={{ color: '#e74c3c', fontSize: '16px' }}>
          NT${Math.max(0, order.totalAmount - (order.totalDiscount || 0)).toFixed(2)}
        </Text>

        {order.totalDiscount && order.totalDiscount > 0 ? (
          <div style={{ marginTop: '4px' }}>
            <Text style={{ fontSize: '12px', color: '#666', textDecoration: 'line-through' }}>
              原价: NT${(order.totalAmount + order.totalDiscount).toFixed(2)}
            </Text>
            <Text style={{ fontSize: '12px', color: '#52c41a', marginLeft: '8px' }}>
              已优惠: NT${order.totalDiscount.toFixed(2)}
            </Text>
          </div>
        ) : ''}
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewOrder(order.id)}
          style={{ borderRadius: '6px' }}
        >
          {t('messages.orders.actions.view')}
        </Button>
        {/* 取消订单按钮 - 只在未发货状态下显示 */}
        {(order.status === 'PENDING' || order.status === 'CONFIRMED' || order.status === 'PROCESSING') && (
          <Button
            type="default"
            size="small"
            danger
            onClick={() => handleCancelOrder(order.id)}
            style={{ borderRadius: '6px' }}
          >
            {t('messages.orders.table.cancelOrder')}
          </Button>
        )}
        {order.status === 'SHIPPED' && (
          <Button
            type="default"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => handleConfirmDelivery(order.id)}
            style={{
              borderRadius: '6px',
              borderColor: '#52c41a',
              color: '#52c41a'
            }}
          >
            {t('messages.orders.confirmDelivery.button')}
          </Button>
        )}
        {order.status === 'DELIVERED' && (
          <>
            <Button
              type="default"
              size="small"
              icon={<StarOutlined />}
              onClick={() => {
                setCurrentOrderForReview(order);
                setReviewModalVisible(true);
              }}
              style={{
                borderRadius: '6px',
                borderColor: '#faad14',
                color: '#faad14'
              }}
            >
              {t('messages.orders.actions.writeReview')}
            </Button>
            <Button
              type="default"
              size="small"
              icon={<ShareAltOutlined />}
              onClick={async () => {
                setCurrentOrderForShare(order);
                const sharesResponse = await apiService.getOrderSocialShares(order.id);
                if (sharesResponse.success) {
                  setExistingShares(sharesResponse.data || []);
                }
                setSocialShareModalVisible(true);
              }}
              style={{
                borderRadius: '6px',
                borderColor: '#1890ff',
                color: '#1890ff'
              }}
            >
              {t('messages.orders.actions.shareExperience')}
            </Button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="order-management" style={{
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        minHeight: '100vh',
        padding: '20px 0',
        marginTop: '80px'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '0 20px'
        }}>
          {/* 訂單列表 */}
          <Card style={{
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '20px 24px 16px',
              borderBottom: '1px solid #f0f0f0',
              background: '#fafafa'
            }}>
              <Title level={4} style={{ margin: 0, color: '#1f2937' }}>
                {t('messages.orders.list.title')}
              </Title>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                {t('messages.orders.list.summary', { count: totalElements })}
              </Text>
            </div>
            {isMobile ? (
              <div>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                  </div>
                ) : (
                  <div>
                    {orders?.map(order => (
                      <MobileOrderCard key={order.id} order={order} />
                    ))}
                    {(!orders || orders.length === 0) && (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        {t('messages.orders.list.empty')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="mobile-table-container">
                <Table
                  columns={columns}
                  dataSource={orders}
                  loading={loading}
                  rowKey="id"
                  pagination={false}
                  scroll={{ x: 800 }}
                  className="modern-table"
                />
              </div>
            )}

            {totalElements > pageSize && (
              <div style={{
                textAlign: 'center',
                padding: '20px 24px',
                borderTop: '1px solid #f0f0f0',
                background: '#fafafa'
              }}>
                <Pagination
                  current={currentPage}
                  total={totalElements}
                  pageSize={pageSize}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  showQuickJumper
                  showTotal={(total, range) =>
                    `顯示 ${range[0]}-${range[1]} 項，共 ${total} 項`
                  }
                />
              </div>
            )}
          </Card>

          {/* 訂單詳情模態框 */}
          <Modal
            title={
              <Space>
                <FileTextOutlined style={{ color: '#17a2b8' }} />
                <span style={{ fontSize: '18px', fontWeight: 600 }}>{t('messages.orders.details.orderNumber')}</span>
              </Space>
            }
            open={detailVisible}
            onCancel={() => setDetailVisible(false)}
            footer={null}
            width={isMobile ? '95%' : 900}
            style={{ top: 20 }}
          >
            {selectedOrder && (
              <div style={{ padding: '8px 0' }}>
                {/* 订单基本信息卡片 */}
                <Card
                  style={{
                    marginBottom: '20px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }}
                  bodyStyle={{ padding: '20px' }}
                >
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    {/* 订单号和状态 */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                          {t('messages.orders.details.orderNumber')}
                        </Text>
                        <Text code style={{ fontSize: '16px', fontWeight: 600, padding: '4px 12px' }}>
                          {selectedOrder.orderNumber}
                        </Text>
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                          {t('messages.orders.details.orderStatus')}
                        </Text>
                        <Tag color={statusMap[selectedOrder.status]?.color} style={{ fontSize: '14px', padding: '4px 12px', margin: 0 }}>
                          {statusMap[selectedOrder.status]?.text}
                        </Tag>
                      </div>
                    </div>

                    <Divider style={{ margin: '16px 0' }} />


                    {/* 地址和时间信息 */}
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                          <EnvironmentOutlined style={{ color: '#17a2b8', marginRight: '8px' }} />
                          <Text strong style={{ fontSize: '14px' }}>{t('messages.orders.details.shippingAddress')}</Text>
                        </div>
                        <Text style={{ fontSize: '14px', color: '#666', display: 'block', paddingLeft: '24px' }}>
                          {selectedOrder.shippingAddress}
                        </Text>
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                          <CalendarOutlined style={{ color: '#17a2b8', marginRight: '8px' }} />
                          <Text strong style={{ fontSize: '14px' }}>{t('messages.orders.details.createdAt')}</Text>
                        </div>
                        <Text style={{ fontSize: '14px', color: '#666', display: 'block', paddingLeft: '24px' }}>
                          {new Date(selectedOrder.createdAt).toLocaleString()}
                        </Text>
                      </div>
                    </div>

                    {selectedOrder.notes && (
                      <div>
                        <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                          {t('messages.orders.details.notes')}
                        </Text>
                        <Text style={{ fontSize: '14px', color: '#666' }}>
                          {selectedOrder.notes}
                        </Text>
                      </div>
                    )}
                  </Space>
                </Card>

                {/* 商品列表 */}
                <Card
                  style={{
                    marginBottom: '20px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }}
                  bodyStyle={{ padding: '20px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                    <ShoppingOutlined style={{ fontSize: '20px', color: '#17a2b8', marginRight: '8px' }} />
                    <Title level={4} style={{ margin: 0 }}>{t('messages.orders.details.productList')}</Title>
                  </div>

                  {(() => {
                    const items = selectedOrder.items || selectedOrder.orderItems || [];
                    const subtotal = items.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 1)), 0);
                    return (
                      <div style={{
                        marginBottom: '20px',
                        padding: '16px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #e9ecef'
                      }}>
                        <Space split={<Divider type="vertical" />} style={{ width: '100%', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <Text type="secondary" style={{ fontSize: '14px' }}>
                            {t('messages.orders.details.productList')}小计: <Text strong style={{ color: '#333' }}>NT${subtotal.toFixed(2)}</Text>
                          </Text>
                          <Text type="secondary" style={{ fontSize: '14px' }}>
                            {t('messages.orders.details.totalAmount')}: <Text strong style={{ color: '#333' }}>NT${selectedOrder.totalAmount.toFixed(2)}</Text>
                          </Text>
                          {selectedOrder.totalDiscount===0?'' : (selectedOrder.totalDiscount && selectedOrder.totalDiscount > 0) && (
                            <Text style={{ fontSize: '14px', color: '#52c41a' }}>
                              {t('messages.orders.details.discount')}: <Text strong>NT${selectedOrder.totalDiscount.toFixed(2)}</Text>
                            </Text>
                          )}
                        </Space>
                      </div>
                    );
                  })()}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {(selectedOrder.items || selectedOrder.orderItems || []).map((item: any, index: number) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '16px',
                          backgroundColor: '#fafafa',
                          borderRadius: '8px',
                          border: '1px solid #f0f0f0'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: '4px' }}>
                            {item.productName}
                          </Text>
                          <Text type="secondary" style={{ fontSize: '13px' }}>
                            {t('messages.orders.details.quantity')}: {item.quantity} × NT${(item.unitPrice || 0).toFixed(2)}
                          </Text>
                        </div>
                        <div style={{ textAlign: 'right', marginLeft: '16px' }}>
                          <Text strong style={{ fontSize: '16px', color: '#17a2b8' }}>
                            NT${(item.price || 0).toFixed(2)}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

              </div>
            )}
          </Modal>

          {/* 社交媒体分享模态框 */}
          <Modal
            title={
              <Space>
                <ShareAltOutlined />
                分享使用体验，获得专属优惠券
              </Space>
            }
            open={socialShareModalVisible}
            onCancel={() => {
              setSocialShareModalVisible(false);
              socialShareForm.resetFields();
              setCurrentOrderForShare(null);
            }}
            footer={null}
            width={600}
          >
            <div style={{ marginBottom: 16, padding: 16, background: '#f6ffed', borderRadius: 6, border: '1px solid #b7eb8f' }}>
              <Text>
                <strong>🎉 恭喜您成功收货！</strong><br />
                现在分享您的使用体验到社交媒体，审核通过后即可获得专属优惠券！
              </Text>
            </div>

            {existingShares.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>已提交的分享记录：</Text>
                <List
                  size="small"
                  dataSource={existingShares}
                  renderItem={(share: any) => (
                    <List.Item>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <div>
                          <Tag color="blue">{share.platform}</Tag>
                          <Tag color={
                            share.status === 'PENDING' ? 'orange' :
                              share.status === 'APPROVED' ? 'green' :
                                share.status === 'COUPON_SENT' ? 'purple' : 'red'
                          }>
                            {share.status === 'PENDING' ? t('messages.orders.socialShare.status.pending') :
                              share.status === 'APPROVED' ? t('messages.orders.socialShare.status.approved') :
                                share.status === 'COUPON_SENT' ? t('messages.orders.socialShare.status.couponSent') : t('messages.orders.socialShare.status.rejected')}
                          </Tag>
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          链接: <a href={share.shareUrl} target="_blank" rel="noopener noreferrer">{share.shareUrl}</a>
                        </Text>
                        {share.description && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>描述: {share.description}</Text>
                        )}
                      </Space>
                    </List.Item>
                  )}
                />
                <Divider />
              </div>
            )}

            <Form
              form={socialShareForm}
              layout="vertical"
              onFinish={handleSocialShareSubmit}
            >
              <Form.Item
                name="platform"
                label={t('messages.orders.socialShare.platformLabel')}
                rules={[{ required: true, message: t('messages.orders.socialShare.platformRequired') }]}
              >
                <Select placeholder={t('messages.orders.socialShare.platformPlaceholder')}>
                  <Option value="facebook">Facebook</Option>
                  <Option value="instagram">Instagram</Option>
                  <Option value="youtube">YouTube</Option>
                  <Option value="tiktok">TikTok</Option>
                  <Option value="twitter">Twitter</Option>
                  <Option value="weibo">微博</Option>
                  <Option value="xiaohongshu">小红书</Option>
                  <Option value="douyin">抖音</Option>
                  <Option value="other">其他</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="shareUrl"
                label={t('messages.orders.socialShare.linkLabel')}
                rules={[
                  { required: true, message: t('messages.orders.socialShare.linkRequired') },
                  { type: 'url', message: t('messages.orders.socialShare.linkInvalid') }
                ]}
              >
                <Input placeholder={t('messages.orders.socialShare.linkPlaceholder')} />
              </Form.Item>

              <Form.Item
                name="description"
                label={t('messages.orders.socialShare.descriptionLabel')}
              >
                <Input.TextArea
                  rows={3}
                  placeholder={t('messages.orders.socialShare.descriptionPlaceholder')}
                />
              </Form.Item>

              <div style={{ textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => {
                    setSocialShareModalVisible(false);
                    socialShareForm.resetFields();
                    setCurrentOrderForShare(null);
                  }}>
                    稍后分享
                  </Button>
                  <Button className="btn-primary-unified" htmlType="submit">
                    提交分享
                  </Button>
                </Space>
              </div>
            </Form>
          </Modal>

          {/* 商品评论模态框 */}
          <Modal
            title={t('messages.orders.review.title')}
            open={reviewModalVisible}
            onCancel={() => {
              setReviewModalVisible(false);
              reviewForm.resetFields();
              setCurrentOrderForReview(null);
              setCurrentReviewGoodsIndex(0);
              setReviewedGoods(new Set());
            }}
            footer={null}
            width={600}
          >
            {currentOrderForReview && (() => {
              const orderItems = currentOrderForReview.items || currentOrderForReview.orderItems || [];
              const currentItem = orderItems[currentReviewGoodsIndex];
              return (
                <>
                  <div style={{ marginBottom: 16, padding: 16, background: '#f6ffed', borderRadius: 6, border: '1px solid #b7eb8f' }}>
                    <Text>
                      <strong>{t('messages.orders.review.congratulations')}</strong><br />
                      {t('messages.orders.review.helpOthers')}
                    </Text>
                  </div>
                  <div style={{ marginBottom: 16, padding: 12, background: '#fff7e6', borderRadius: 6, border: '1px solid #ffd591' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      💡 {t('messages.orders.review.updateHint')}
                    </Text>
                  </div>
                  {orderItems.length > 1 && (
                    <div style={{ marginBottom: 16, padding: 12, background: '#e6f7ff', borderRadius: 6, border: '1px solid #91d5ff' }}>
                      <div style={{ marginBottom: 8 }}>
                        <Text>
                          {t('messages.orders.review.progressInfo', {
                            total: orderItems.length,
                            current: currentReviewGoodsIndex + 1
                          })}
                        </Text>
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {orderItems.map((_, index) => (
                          <div
                            key={index}
                            style={{
                              flex: 1,
                              height: '6px',
                              borderRadius: '3px',
                              backgroundColor: reviewedGoods.has(index)
                                ? '#52c41a'
                                : index === currentReviewGoodsIndex
                                  ? '#1890ff'
                                  : '#d9d9d9'
                            }}
                            title={
                              reviewedGoods.has(index)
                                ? t('messages.orders.review.reviewed')
                                : index === currentReviewGoodsIndex
                                  ? t('messages.orders.review.current')
                                  : t('messages.orders.review.notReviewed')
                            }
                          />
                        ))}
                      </div>
                      <div style={{ marginTop: 6, fontSize: '12px', color: '#666' }}>
                        <Text type="secondary">
                          {t('messages.orders.review.reviewedCount', {
                            reviewed: reviewedGoods.size,
                            total: orderItems.length
                          })}
                        </Text>
                      </div>
                    </div>
                  )}
                  {currentItem && (
                    <div style={{ marginBottom: 16, padding: 12, background: '#fff7e6', borderRadius: 6, border: '1px solid #ffd591' }}>
                      <Text strong>{t('messages.orders.review.currentProduct')}</Text>
                      <div style={{ marginTop: 8 }}>
                        <Text>{currentItem.productName || t('messages.orders.review.product')}</Text>
                        {currentItem.productSku && (
                          <Text type="secondary" style={{ marginLeft: 8 }}>
                            ({currentItem.productSku})
                          </Text>
                        )}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            <Form
              form={reviewForm}
              layout="vertical"
              onFinish={handleReviewSubmit}
            >
              <Form.Item
                label={t('messages.orders.review.ratingLabel')}
                name="rating"
                rules={[{ required: true, message: t('messages.orders.review.ratingRequired') }]}
              >
                <Rate />
              </Form.Item>

              <Form.Item
                label={t('messages.orders.review.titleLabel')}
                name="title"
              >
                <Input placeholder={t('messages.orders.review.titlePlaceholder')} />
              </Form.Item>

              <Form.Item
                label={t('messages.orders.review.contentLabel')}
                name="content"
                rules={[{ required: true, message: t('messages.orders.review.contentRequired') }]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder={t('messages.orders.review.contentPlaceholder')}
                />
              </Form.Item>

              <Form.Item name="isAnonymous" valuePropName="checked">
                <Checkbox>{t('messages.orders.review.anonymous')}</Checkbox>
              </Form.Item>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  {currentOrderForReview && (() => {
                    const orderItems = currentOrderForReview.items || currentOrderForReview.orderItems || [];
                    return orderItems.length > 1 && (
                      <>
                        <Button
                          onClick={() => {
                            if (currentReviewGoodsIndex > 0) {
                              setCurrentReviewGoodsIndex(currentReviewGoodsIndex - 1);
                              reviewForm.resetFields();
                            }
                          }}
                          disabled={currentReviewGoodsIndex === 0}
                        >
                          {t('messages.orders.review.previousProduct')}
                        </Button>
                        <Button
                          onClick={() => {
                            const nextIndex = currentReviewGoodsIndex + 1;
                            if (nextIndex < orderItems.length) {
                              setCurrentReviewGoodsIndex(nextIndex);
                              reviewForm.resetFields();
                              message.info(t('messages.orders.review.skippedToNext', { next: nextIndex + 1 }));
                            } else {
                              message.success(t('messages.orders.review.allCompleted'));
                              setReviewModalVisible(false);
                              setCurrentReviewGoodsIndex(0);
                              setReviewedGoods(new Set());
                            }
                          }}
                        >
                          {t('messages.orders.review.skipProduct')}
                        </Button>
                      </>
                    );
                  })()}
                </Space>
                <Space>
                  <Button onClick={() => {
                    setReviewModalVisible(false);
                    reviewForm.resetFields();
                    setCurrentOrderForReview(null);
                    setCurrentReviewGoodsIndex(0);
                    setReviewedGoods(new Set());
                  }}>
                    {t('messages.orders.review.reviewLater')}
                  </Button>
                  <Button className="btn-primary-unified" htmlType="submit">
                    {t('messages.orders.review.submitReview')}
                  </Button>
                </Space>
              </div>
            </Form>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default OrderManagement;
